import { PlayerSearchPanel } from "@/components/organisms/NavigationBar/NavBarDesktop/components/NavPlayerSearch";
import { cn } from "@/lib/utils";
import { addAlert } from "@/redux/message/messageSlice";
import { SearchedPlayer } from "@/redux/players/SearchedPlayer.type";
import { AppDispatch } from "@/redux/store";
import { useTranslation } from "@repo/shared/i18n";
import {
  ManuallyAddPlayerProperties,
  addPlayerToClub,
  myPlayersSelector,
  updateManuallyAddPlayerProperties,
} from "@repo/shared/redux";
import { useDispatch, useSelector } from "react-redux";

const MARKS: (keyof ManuallyAddPlayerProperties)[] = [
  "untradeable",
  "transferList",
  "activeSquad",
];

/**
 * Adding players to the club: the app's player search, with a + on every
 * result. What the added players are marked as (untradeable, on the transfer
 * list, in the active squad) is picked once above the field.
 */
export const AddPlayersSearch = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation(["myClub", "common"]);
  const dispatch = useDispatch<AppDispatch>();
  const { manuallyAddPlayerProperties: marks } = useSelector(myPlayersSelector);

  return (
    <PlayerSearchPanel
      onClose={onClose}
      onAdd={async (player) => {
        try {
          await dispatch(
            addPlayerToClub(player as unknown as SearchedPlayer),
          ).unwrap();
        } catch (error) {
          dispatch(
            addAlert({ type: "error", description: t("common:error.generic") }),
          );
          throw error;
        }
      }}
      header={
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <h2 className="mr-auto text-base font-semibold">
            {t("players.addTitle")}
          </h2>
          <span className="text-xs text-foreground-muted">
            {t("players.markPlayerAs")}
          </span>
          {MARKS.map((mark) => (
            <button
              key={mark}
              type="button"
              aria-pressed={marks[mark]}
              onClick={() =>
                dispatch(
                  updateManuallyAddPlayerProperties({
                    ...marks,
                    [mark]: !marks[mark],
                  }),
                )
              }
              className={cn(
                "h-7 rounded-full border px-2.5 text-xs font-medium transition-colors",
                marks[mark]
                  ? "border-primary-border bg-primary/20 text-neutral-50"
                  : "border-neutral-700 text-neutral-300 hover:bg-neutral-750",
              )}
            >
              {t(`players.property.${mark}`)}
            </button>
          ))}
        </div>
      }
    />
  );
};
